import { useFormikContext } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";

import { readAndValidateImage } from "@/components/screens/ProductFormPage/helpers";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import useToast from "@/contexts/toast-context";
import { isValidFileType } from "@/helpers/file-helpers";
import useDialog from "@/hooks/use-dialog";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ImageData } from "@/interfaces/product.interface";
import { ProductFormMessages } from "@/messages/products/product-form.messages";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";
import { useSkuImageValidation } from "@/hooks/use-sku-image-validation";

type Props = {
  messages: ProductFormMessages;
  maximumOfPhotos?: number;
};

function useProductImages({ messages, maximumOfPhotos = 10 }: Props) {
  const { values, setFieldValue } = useFormikContext<ProductFormData>();
  const { showToast } = useToast();
  const { isVisible, handleOpen, handleClose } = useDialog();
  const [imageToDelete, setImageToDelete] = useState<IProductImage | ImageData | null>(null);
  const { ensureProductSaved, reloadImages, savedImages, setImagesToDelete } =
    useProductFormStateContext();
  const { getSkusUsingImage } = useSkuImageValidation();

  // Memoize productImages to avoid unnecessary re-renders
  const productImages = useMemo(() => values.productImages ?? [], [values.productImages]);

  // Initialize the main image as the first one marked as `main`
  const initialMainImage = useMemo(
    () => productImages.find((img) => img.main) || null,
    [productImages]
  );
  const [mainImage, setMainImage] = useState<IProductImage | ImageData | null>(initialMainImage);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (productImages.length + acceptedFiles.length > maximumOfPhotos) {
        showToast({ text: messages.maximumOfPhotos, type: "error" });
        return;
      }

      // Verificar se tem campos obrigatórios preenchidos
      const hasRequiredFields = values.code && values.title;

      // Se for primeira imagem E tem campos obrigatórios, garantir que produto está salvo
      if (productImages.length === 0 && hasRequiredFields) {
        const saved = await ensureProductSaved();
        if (!saved) {
          return;
        }
      }

      // Se não tem campos obrigatórios, apenas adiciona imagem localmente (preview)
      // O produto será salvo automaticamente quando os campos forem preenchidos

      acceptedFiles.forEach((file) => {
        if (!isValidFileType(file.name)) {
          showToast({ text: messages.unsupportedFileFormat, type: "error" });
          return;
        }

        readAndValidateImage(
          messages,
          file,
          (imgPreview) => {
            const { preview } = imgPreview;
            const url = typeof preview === "string" ? imgPreview.preview : "";
            const previewImg = typeof preview === "string" ? imgPreview.preview : undefined;

            const newImage: ImageData = {
              ...imgPreview,
              url: url?.toString() || "",
              preview: previewImg?.toString(),
              main: productImages.length === 0, // Set as main if it's the first image
            };

            if (newImage.main) {
              setMainImage(newImage);
            }

            setFieldValue("productImages", [...productImages, newImage]);
          },
          (errorMessage) => showToast({ text: errorMessage, type: "error" })
        );
      });
    },
    [
      productImages,
      showToast,
      maximumOfPhotos,
      messages,
      setFieldValue,
      ensureProductSaved,
      values.code,
      values.title,
    ]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (imageToDelete) {
      const updatedImages = productImages.filter((img) => img !== imageToDelete);
      setFieldValue("productImages", updatedImages);

      // Se a imagem tem ID (já foi salva no backend), marcar para exclusão
      if (imageToDelete && "id" in imageToDelete && imageToDelete.id !== undefined) {
        setImagesToDelete((prev) => [...prev, String(imageToDelete.id)]);
      } else {
        // Se a imagem não tem ID (ainda local), verificar se existe no savedImages por nome
        // Isso acontece quando a imagem foi enviada mas o Formik não foi atualizado
        const savedImage = savedImages.find((saved) => saved.name === imageToDelete.name);
        if (savedImage && savedImage.id) {
          setImagesToDelete((prev) => [...prev, String(savedImage.id)]);
        }
      }

      // Se a imagem era a principal, definir a próxima como principal
      if (imageToDelete.main) {
        if (updatedImages.length > 0) {
          const nextMainImage = updatedImages[0];
          nextMainImage.main = true;
          setMainImage(nextMainImage);
        } else {
          setMainImage(null);
        }
      }

      // Limpar a imagem deletada de todos os SKUs que a estavam usando
      const deletedPhysicalAddress =
        "phisicalAddress" in imageToDelete ? imageToDelete.phisicalAddress : null;

      if (deletedPhysicalAddress && values.skus) {
        const updatedSkus = values.skus.map((sku) => {
          if (sku.imagePhisicalAddress === deletedPhysicalAddress) {
            return { ...sku, imagePhisicalAddress: "", imageUrl: "" };
          }
          return sku;
        });
        setFieldValue("skus", updatedSkus);
      }

      // Recarregar savedImages para refletir no modal de SKU
      if (reloadImages) {
        await reloadImages();

        // Aguardar propagação do estado
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    setImageToDelete(null);
    handleClose();
  }, [
    imageToDelete,
    productImages,
    values.skus,
    savedImages,
    setFieldValue,
    setImagesToDelete,
    handleClose,
    reloadImages,
  ]);

  const promptRemoveImage = useCallback(
    (image: ImageData | IProductImage, event: React.MouseEvent) => {
      event.stopPropagation();
      const correctImage =
        productImages.find(
          (img) => img.url === image.url && img.preview === image.preview && img.main === image.main
        ) || image;
      setImageToDelete(correctImage);
      handleOpen();
    },
    [productImages, handleOpen]
  );

  const handleSetMainImage = useCallback(
    (image: IProductImage) => {
      const updatedImages = productImages.map((img) =>
        img.id && image.id
          ? img.id === image.id
            ? { ...img, main: true }
            : { ...img, main: false }
          : img.preview === image.preview
            ? { ...img, main: true }
            : { ...img, main: false }
      );

      setFieldValue("productImages", updatedImages);
      setMainImage({ ...image, main: true });
    },
    [productImages, setFieldValue]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uniqueImages = useMemo(() => productImages.filter((img) => !img.main), [productImages]);

  // Obter SKUs afetados pela imagem a ser deletada
  const affectedSkus = useMemo(() => {
    if (!imageToDelete || !("phisicalAddress" in imageToDelete)) return [];
    return getSkusUsingImage(imageToDelete.phisicalAddress);
  }, [imageToDelete, getSkusUsingImage]);

  return {
    mainImage,
    uniqueImages,
    getRootProps,
    getInputProps,
    promptRemoveImage,
    handleSetMainImage,
    isVisible,
    handleClose,
    handleConfirmDelete,
    imageToDelete,
    affectedSkus,
  };
}

export default useProductImages;
